import Button from '@ingka/button';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import { translate } from '@repo/phrase';
import type { AssetDataType } from '@repo/types/asset';
import { isDraft } from '@repo/types/draft';
import { isQuotation } from '@repo/types/quotation';
import { getStorePrimaryLocale } from '@repo/utils';

import { BarCode } from '@/components/share/barcode';
import { ShareOptions } from '@/components/share/share-options';
import { useShareEventType, useShareForm, useShareMutation } from '@/components/share/share.hooks';

import { useAssetParams } from '@/hooks/asset.hooks';
import { useCustomerDetails } from '@/hooks/customer-details.hooks';

import styles from './styles.module.scss';

interface Props {
    visible: boolean;
    onClose: () => void;
    data: AssetDataType;
    initialEmail?: string | undefined;
    initialSms?: string | undefined;
}

export const ShareModal = ({ visible, onClose, data, initialEmail, initialSms }: Props) => {
    const { assetId, assetType } = useAssetParams();
    const { isRegisteredCustomer, isRimsCustomer, preferredLocale } = useCustomerDetails(data);
    const storePrimaryLocale = getStorePrimaryLocale();

    const isDraftOrder = isDraft(data);
    const isQuotationOrder = isQuotation(data);

    const {
        email,
        hasSelectedShareOption,
        locale,
        printer,
        setEmail,
        setLocale,
        setPrinter,
        setSms,
        shouldValidate,
        sms,
        supportedLocales,
        validate,
    } = useShareForm({
        data,
        initialEmail,
        initialSms,
        preferredLocale,
        storePrimaryLocale,
    });

    const eventType = useShareEventType(data);

    const { mutate, isPending } = useShareMutation({
        orderId: assetId,
        assetType,
        data,
        email,
        sms,
        printer,
        locale,
        customerDetails: { isRegisteredCustomer, isRimsCustomer },
        onClose,
        eventType,
    });
    const onSubmit = () => {
        if (validate()) {
            mutate();
        }
    };

    return (
        <Modal handleCloseBtn={onClose} visible={visible} data-testid="share-modal">
            <Sheets
                aria-label={translate('share_modal_option')}
                header={<ModalHeader title={translate('share_modal_option')} />}
                footer={
                    <ModalFooter>
                        <Button
                            text={translate('share_modal_share')}
                            type="primary"
                            disabled={!hasSelectedShareOption}
                            loading={isPending}
                            onClick={onSubmit}
                        />
                    </ModalFooter>
                }
            >
                <ModalBody className={String(styles.container)}>
                    {!isDraftOrder && !isQuotationOrder && <BarCode imgOnly />}
                    <ShareOptions
                        data={data}
                        email={email}
                        sms={sms}
                        locale={locale}
                        printer={printer}
                        setEmail={setEmail}
                        setLocale={setLocale}
                        setSms={setSms}
                        setPrinter={setPrinter}
                        shouldValidate={shouldValidate}
                        supportedLocales={supportedLocales}
                        divider
                    />
                </ModalBody>
            </Sheets>
        </Modal>
    );
};
